import { HDate, HebrewCalendar, gematriya } from '@hebcal/core';
import { mapHebcalParasha, getParashaByEnglish } from './parashaNameMap';

export interface ParashaResult {
  hebBirth: HDate;
  hebBM: HDate;
  civilBM: Date;
  shabbat: Date;
  parshiot: string[]; // Array of "Parashat X" strings
  otherReading: string[];
}

// Helper: create a date at local noon (avoids timezone/day-boundary issues and being before halachic day start)
function normalizeDateForCalendar(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
}

// Parse dd/mm/yyyy (supports / . - separators)
export function parseEuropeanDate(input: string): Date | null {
  const parts = input.trim().split(/[\\/.-]/);
  if (parts.length !== 3) return null;
  const [dd, mm, yyyy] = parts.map((p) => parseInt(p, 10));
  if (!dd || !mm || !yyyy) return null;
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  // Use local date (not UTC) then normalize to noon for stability
  return normalizeDateForCalendar(new Date(yyyy, mm - 1, dd));
}

export function nextShabbatOnOrAfter(d: Date): Date {
  // Work with local date component only
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = date.getDay();
  const delta = (6 - day + 7) % 7; // days until Saturday
  date.setDate(date.getDate() + delta);
  return normalizeDateForCalendar(date);
}

export function addHebrewYears(h: HDate, years: number): HDate {
  const day = h.getDate();
  const month = h.getMonth();
  const year = h.getFullYear() + years;
  return new HDate(day, month, year);
}

export function getParshiotOnDate(date: Date, il: boolean): string[] {
  const day = normalizeDateForCalendar(date);
  const events = HebrewCalendar.calendar({
    start: day,
    end: day,
    il,
    sedrot: true,
  });
  return events
    .map((e) => (e as any).getDesc?.() || '')
    .filter((desc) => desc.startsWith('Parashat '));
}

export function getOtherReadingDescription(date: Date, il: boolean): string[] {
  const day = normalizeDateForCalendar(date);
  const events = HebrewCalendar.calendar({
    start: day,
    end: day,
    il,
    sedrot: true,
  });
  return events
    .map((e) => (e as any).getDesc?.() || '')
    .filter(
      (d) =>
        d &&
        !d.toLowerCase().includes('candle') &&
        !d.toLowerCase().includes('havdalah') &&
        !d.toLowerCase().includes('omer') &&
        !d.startsWith('Parashat '),
    );
}

export function computeBarMitzvahData(
  gregDateStr: string,
  opts?: { afterSunset?: boolean; israelSchedule?: boolean },
): ParashaResult | null {
  const civil = parseEuropeanDate(gregDateStr);
  if (!civil) return null;
  const { afterSunset = false, israelSchedule = false } = opts || {};

  // If birth was after sunset, Hebrew date is next day
  const civilForHebrew = new Date(civil.getTime());
  if (afterSunset) civilForHebrew.setDate(civilForHebrew.getDate() + 1);

  const hebBirth = new HDate(civilForHebrew);
  const hebBM = addHebrewYears(hebBirth, 13);
  const civilBM = normalizeDateForCalendar(hebBM.greg());
  const shabbat = nextShabbatOnOrAfter(civilBM);

  let parshiot = getParshiotOnDate(shabbat, israelSchedule);

  // Fallback: if for some reason no parsha returned, try the following day (edge case around timezone/day shift)
  if (parshiot.length === 0) {
    const nextDay = new Date(shabbat.getFullYear(), shabbat.getMonth(), shabbat.getDate() + 1, 12);
    parshiot = getParshiotOnDate(nextDay, israelSchedule);
  }

  // Stronger fallback: scan the upcoming week to guarantee at least one parasha
  if (parshiot.length === 0) {
    const weekEnd = new Date(shabbat.getFullYear(), shabbat.getMonth(), shabbat.getDate() + 6, 12);
    const weekEvents = HebrewCalendar.calendar({
      start: shabbat,
      end: weekEnd,
      il: israelSchedule,
      sedrot: true,
    });
    const weeklyParshiot = weekEvents
      .filter((e) => {
        const desc = (e as any).getDesc?.() || '';
        if (!desc.startsWith('Parashat ')) return false;
        // Ensure it's the Shabbat reading (HebrewCalendar may list sedra only on Shabbat anyway)
        const g = (e as any).date?.greg?.();
        return g instanceof Date ? g.getDay() === 6 : true;
      })
      .map((e) => (e as any).getDesc());
    if (weeklyParshiot.length > 0) parshiot = weeklyParshiot.slice(0, 1); // take the first relevant parasha
  }

  const otherReading =
    parshiot.length === 0 ? getOtherReadingDescription(shabbat, israelSchedule) : [];

  return { hebBirth, hebBM, civilBM, shabbat, parshiot, otherReading };
}

export function formatHebrew(h?: HDate): string {
  return h ? h.renderGematriya() : '';
}

/** Like formatHebrew but always prefixes the thousands digit (e.g. "ה'ת״ת" for year 5800) so uncommon years like 5800 are unambiguous. */
export function formatHebrewWithThousands(d: Date): string {
  const h = new HDate(d);
  const year = h.getFullYear();
  const thousands = Math.floor(year / 1000);
  const rest = year % 1000;
  const thousandsLetter = gematriya(thousands).replace(/[׳״]/g, '');
  const fullYear = `${thousandsLetter}'${gematriya(rest)}`;
  const parts = h.renderGematriya(true).split(' ');
  return parts.length >= 3 ? `${parts[0]} ${parts[1]} ${fullYear}` : h.renderGematriya();
}
export function formatCivil(d?: Date): string {
  return d
    ? d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';
}

/**
 * Find the next date when a specific parasha will be read.
 * Searches from today through the next 2 Jewish years to find the next occurrence.
 * Uses the same canonical parasha mapping as the app (parashaNameMap) so Hebcal
 * spellings (e.g. "Bereishit") match our names (e.g. "Bereshit").
 * @param parashaName - The parasha name (e.g., "Bereshit") from user selection
 * @param israelSchedule - Whether to use Israel's Torah reading schedule
 * @returns The next date when this parasha will be read, or null if not found
 */
export function findNextParashaDate(
  parashaName: string,
  israelSchedule: boolean = false,
): Date | null {
  const ourParasha = getParashaByEnglish(parashaName);
  if (!ourParasha) return null;

  const today = new Date();
  const endDate = new Date(today);
  endDate.setFullYear(endDate.getFullYear() + 2);

  const events = HebrewCalendar.calendar({
    start: today,
    end: endDate,
    il: israelSchedule,
    sedrot: true,
  });

  for (const event of events) {
    const desc = (event as any).getDesc?.() || '';
    if (!desc.startsWith('Parashat ')) continue;

    const mapped = mapHebcalParasha(desc);
    if (mapped && mapped.english === ourParasha.english) {
      const eventDate = (event as any).date?.greg?.();
      if (eventDate instanceof Date) {
        return normalizeDateForCalendar(eventDate);
      }
    }
  }

  return null;
}

/**
 * Calculate days remaining until a target date.
 * @param targetDate - The target date
 * @returns Number of days remaining (0 if today or past)
 */
export function calculateDaysUntil(targetDate: Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(targetDate);
  target.setHours(0, 0, 0, 0);

  const diffMs = target.getTime() - today.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

/**
 * Parse a dd/mm/yyyy date string and return a Date object.
 */
export function parseDateString(dateStr: string): Date | null {
  const parts = dateStr.split('/');
  if (parts.length !== 3) return null;

  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
  const year = parseInt(parts[2], 10);

  if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

  return new Date(year, month, day);
}
